// ==============================
// Imports
// ==============================

// MUI Components
import {
  Delete,
  Edit,
  FileDownload,
  PersonAdd,
  Search,
} from '@mui/icons-material';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Stack,
  IconButton,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import { TableNoData, TableSpacerRow } from './common/TableEmptyState';
import TableLoader from './common/TableLoader';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

// Local libraries
import { useState } from 'react';

// Hook
import { useCoachController } from '../hooks/useCoachTable';
import CreateCoachDialog from '../components/FormDialog/CreateCoachDialog';
import UpdateCoachDialog from '../components/FormDialog/UpdateCoachDialog';
import {
  rowHoverBg,
  selectedRowBg,
  selectedRowBgOpaque,
} from '../utils/rowHighlight';
import { useTranslation } from '../i18n';

const CoachTable = () => {
  const { t } = useTranslation();
  const {
    coaches,
    page,
    limit,
    totalItems,
    searchTerm,
    emptyRows,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    clearFilters,
    fetchCoaches,
    handleDownloadCoaches,
    downloadLoading,
    loading,
    setPage,
    selectedCoach,
    setSelectedCoach,
    handleDeleteCoach,
    openDeleteCoachDialog,
    setOpenDeleteCoachDialog,
    handleUpdateCoach,
    openCreateCoachDialog,
    setOpenCreateCoachDialog,
    lastSyncTime,
    highlightedRow,
    setHighlightedRow,
  } = useCoachController();

  const [openUpdateCoachDialog, setOpenUpdateCoachDialog] = useState(false);

  // ==============================
  // Render
  // ==============================
  return (
    <>
      {/* Add Coach — outside the table, under the page divider (like other screens) */}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenCreateCoachDialog(true)}
          startIcon={<PersonAdd />}
          sx={{ color: 'white' }}
        >
          {t('coach.addCoach')}
        </Button>
      </Box>
      <Paper>
        <Box
          p={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <TextField
            size="medium"
            placeholder={t('coach.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                fetchCoaches();
                setPage(1);
              }
            }}
            sx={{ width: 500 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                size: 'small',
              },
            }}
          />

          <Box display="flex" alignItems="center" gap={1}>
            {/* Clear filters button */}
            {searchTerm ? (
              <Button
                size="small"
                variant="contained"
                color="info"
                onClick={clearFilters}
                startIcon={<FilterAltOffIcon />}
              >
                {t('coach.clear')}
              </Button>
            ) : null}

            <Tooltip title={t('coach.downloadList')}>
              <IconButton
                onClick={handleDownloadCoaches}
                loading={downloadLoading}
                disabled={totalItems === 0 || downloadLoading}
              >
                <FileDownload />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <TableContainer>
          <Table size="small" padding="checkbox">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#4da7db' }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('coach.colId')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('coach.colName')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('coach.colEmail')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography fontWeight="bold" color="#fff">
                    {t('coach.colCreatedAt')}
                  </Typography>
                </TableCell>

                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                    position: 'sticky',
                    right: -16,
                    zIndex: '1 !important',
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    color="#fff"
                    bgcolor={'#4da7db'}
                    borderLeft={1}
                    textAlign="center"
                    className="!px-2"
                  >
                    {t('coach.colActions')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            {/* ********** */}
            {/* DATA MANIPULATION */}
            {/* ********** */}
            <TableBody>
              {loading ? (
                <TableLoader colSpan={5} />
              ) : (
                <>
                  {coaches.map((coach) => (
                <TableRow
                  key={coach.id}
                  sx={{
                    '&:hover': { backgroundColor: rowHoverBg },
                    backgroundColor:
                      highlightedRow === `data_${coach.id}`
                        ? selectedRowBg
                        : 'inherit',
                  }}
                  onClick={() => {
                    if (highlightedRow === `data_${coach.id}`) {
                      setHighlightedRow(null);
                    } else {
                      setHighlightedRow(`data_${coach.id}`);
                    }
                  }}
                >
                  <TableCell>{coach.id}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {coach.name}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {coach.email}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(coach.createdAt).toLocaleDateString()}
                  </TableCell>

                  {/* Actions */}
                  <TableCell
                    sx={{
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      right: -16,
                      zIndex: '1 !important',
                      backgroundColor:
                        highlightedRow === `data_${coach.id}`
                          ? selectedRowBgOpaque
                          : 'background.paper',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="center"
                      className="!pl-1 !pr-2"
                    >
                      {/* Edit */}
                      <Tooltip title={t('coach.edit')} placement="top">
                        <IconButton
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCoach(coach);
                            setOpenUpdateCoachDialog(true);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      {/* Delete */}
                      <Tooltip title={t('coach.delete')} placement="top">
                        <IconButton
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCoach(coach);
                            setOpenDeleteCoachDialog(true);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
                  {coaches.length === 0 ? (
                    <TableNoData message="No coaches found" />
                  ) : (
                    <TableSpacerRow rows={emptyRows} />
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          display="flex"
          flexDirection={'row'}
          justifyContent="space-between"
          alignItems="center"
        >
          <Box display="flex" alignItems="center" gap={1} p={2}>
            <Typography variant="body2" color="textSecondary">
              {t('coach.lastSynced')}:{' '}
              {lastSyncTime
                ? lastSyncTime.format('DD/MM/YYYY HH:mm:ss')
                : 'N/A'}
            </Typography>
          </Box>

          <TablePagination
            component="div"
            count={totalItems}
            page={page - 1}
            onPageChange={handleChangePage}
            rowsPerPage={limit}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </Box>

        {/* Delete Coach Dialog */}
        <Dialog
          open={openDeleteCoachDialog}
          onClose={() => setOpenDeleteCoachDialog(false)}
        >
          <DialogTitle>{t('coach.deleteTitle')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('coach.deleteConfirm')} <b>{selectedCoach?.name}</b> ?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteCoachDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                if (selectedCoach) handleDeleteCoach(selectedCoach);
                setOpenDeleteCoachDialog(false);
              }}
              color="error"
              variant="contained"
            >
              {t('coach.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Update Coach Dialog */}
        <UpdateCoachDialog
          open={openUpdateCoachDialog}
          handleClose={() => {
            setOpenUpdateCoachDialog(false);
            setSelectedCoach(null);
          }}
          coach={selectedCoach}
          handleUpdateCoach={handleUpdateCoach}
        />

        {/* Create Coach Dialog */}
        <CreateCoachDialog
          open={openCreateCoachDialog}
          handleClose={() => setOpenCreateCoachDialog(false)}
          fetchCoaches={fetchCoaches}
        />
      </Paper>
    </>
  );
};

export default CoachTable;
